import { HttpStatusCode } from "axios"
import { create } from "zustand"
import { getUserInfo, login, logout } from "@httpServices/auth"
import { mergeCore, partializeCore } from "@stores/core"
import { appNavigate, convertPermissionToMappedPermission } from "@utils/helpers"
import { ROUTE } from "@constants"
import { AuthInformation, PermissionString, Role, User, ResponseBody } from "@/types"
import { Credential } from "@/types/user"
import { persist } from "zustand/middleware"

type AuthState = {
  user: Partial<User>
  permissions: Partial<Record<PermissionString, boolean>>
  roles: Role["name"][]
  role_ids: number[]
  access_token: string
  clientId: number | null
  actions: {
    login: (credentials: Credential) => Promise<ResponseBody<AuthInformation>>
    logout: () => Promise<ResponseBody<any>>
    getUserInfo: () => Promise<ResponseBody<Omit<AuthInformation, "access_token">>>
    resetToken: () => void
  }
}

const initState: Omit<AuthState, "actions"> = {
  access_token: "",
  user: {},
  roles: [],
  role_ids: [],
  permissions: {},
  clientId: null,
}

export const useAuthStore = create(
  persist<AuthState, [], [], Omit<AuthState, "actions">>(
    (set) => ({
      ...initState,
      actions: {
        login: async (credentials: Credential) => {
          const response = await login(credentials)

          if (response.code === HttpStatusCode.Ok) {
            const permissions = convertPermissionToMappedPermission(response.data.permissions)

            set({ ...response.data, permissions })
          }

          return response
        },
        logout: async () => {
          const response = await logout()

          if (response.code === HttpStatusCode.Ok) {
            set({ ...initState })
            appNavigate(ROUTE.LOGIN)
          }

          return response
        },
        getUserInfo: async () => {
          const response = await getUserInfo()
          const permissions = convertPermissionToMappedPermission(response.data.permissions)

          if (response.code === HttpStatusCode.Ok) {
            set({ ...response.data, permissions })
          } else {
            set({ ...initState })
          }

          return response
        },
        resetToken: () => {
          set({ ...initState })
        },
      },
    }),
    {
      name: "auth-store",
      partialize: (state) => partializeCore(state, ["actions"]),
      merge: mergeCore,
    }
  )
)
